import { useState } from 'react'
import type { ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  useHealth,
  useSecurity,
  useApprovals,
  useTasks,
  useSelfAuditSummary,
  useActionLog,
  useIncidents,
  useMcpCatalogue,
  useGithubStatus,
  useCfConnectionStatus,
  useWordpressDeepStatus,
  useVpsResourceUsage,
  useDeploymentsByDay,
  usePlatformModel,
  usePipelineQueue,
} from '@/hooks/useApi'
import { useAppStore } from '@/store'
import type { SidebarSection } from '@/types'
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Circle,
  ClipboardList,
  Cloud,
  Code2,
  Cpu,
  Globe,
  HardDrive,
  Link2,
  ListTodo,
  RefreshCw,
  Rocket,
  Server,
  ShieldCheck,
  UserCog,
  XCircle,
  Zap,
} from 'lucide-react'

function GlassCard({
  children,
  className = '',
  onClick,
  accent,
}: {
  children: ReactNode
  className?: string
  onClick?: () => void
  accent?: string
}) {
  return (
    <div
      onClick={onClick}
      className={`relative overflow-hidden rounded-[1.4rem] border p-4 backdrop-blur-xl transition sm:p-5 ${onClick ? 'cursor-pointer hover:-translate-y-0.5' : ''} ${className}`}
      style={{
        background: 'linear-gradient(135deg, var(--card-bg-start), var(--card-bg-end))',
        borderColor: 'var(--card-border)',
        boxShadow: 'var(--card-shadow)',
      }}
    >
      {accent && (
        <div
          className="absolute inset-x-0 top-0 h-[2px] opacity-75"
          style={{ background: accent }}
        />
      )}
      <div className="relative">{children}</div>
    </div>
  )
}

function SectionHeading({
  title,
  description,
  action,
  onAction,
}: {
  title: string
  description?: string
  action?: string
  onAction?: () => void
}) {
  return (
    <div className="mb-3 flex items-end justify-between gap-3">
      <div className="min-w-0">
        <h2 className="text-[13px] font-semibold uppercase tracking-[0.12em] text-text-primary">
          {title}
        </h2>
        {description && <p className="mt-1 text-[11px] text-text-muted">{description}</p>}
      </div>
      {action && onAction && (
        <button
          onClick={onAction}
          className="inline-flex shrink-0 items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-medium text-accent transition hover:bg-accent/8"
        >
          {action} <ChevronRight className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  )
}

function MetricCard({
  icon: Icon,
  label,
  value,
  detail,
  color,
  onClick,
  unavailable = false,
}: {
  icon: typeof Activity
  label: string
  value: string | number
  detail: string
  color: string
  onClick?: () => void
  unavailable?: boolean
}) {
  return (
    <GlassCard onClick={onClick} accent={color} className="min-h-[130px]">
      <div className="flex h-full flex-col justify-between gap-4">
        <div className="flex items-start justify-between gap-3">
          <span
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border"
            style={{ background: `${color}16`, borderColor: `${color}2b` }}
          >
            <Icon className="h-4 w-4" style={{ color }} />
          </span>
          {unavailable && (
            <span className="rounded-full border border-amber-500/20 bg-amber-500/8 px-2 py-1 text-[9px] font-semibold uppercase tracking-[0.08em] text-amber-600">
              Unavailable
            </span>
          )}
        </div>
        <div>
          <div className="text-2xl font-semibold tracking-tight text-text-primary">{value}</div>
          <div className="mt-1 text-xs font-medium text-text-secondary">{label}</div>
          <div className="mt-1 text-[10px] leading-relaxed text-text-muted">{detail}</div>
        </div>
      </div>
    </GlassCard>
  )
}

function Dot({ status }: { status: string }) {
  const color =
    status === 'healthy' ? '#22c55e'
      : status === 'degraded' ? '#f59e0b'
        : status === 'error' || status === 'critical' ? '#ef4444'
          : '#94a3b8'
  return <Circle className="h-2 w-2 shrink-0" style={{ color, fill: color }} />
}

function prettyTime(value?: string | null) {
  if (!value) return 'Not available'
  try {
    return new Date(value).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return value
  }
}

function shortTime(value?: string | null) {
  if (!value) return ''
  try {
    return new Date(value).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  } catch {
    return ''
  }
}

function prettifyAction(action: string, target?: string | null) {
  const labels: Record<string, string> = {
    write_file: 'File written',
    patch_file: 'File updated',
    vps_patch_file: 'File updated',
    vps_deploy: 'Deployment completed',
    deploy_worker: 'Deployment completed',
    vps_create_file: 'File created',
    vps_delete_file: 'File deleted',
    vps_move_file: 'File moved',
    vps_copy_file: 'File copied',
    write_r2_memory: 'Knowledge updated',
    patch_r2_memory: 'Knowledge updated',
    create_branch: 'Branch created',
    create_pull_request: 'Pull request opened',
    log_event: 'Event logged',
    log_decision: 'Decision recorded',
  }
  const label = labels[action] || action.replaceAll('_', ' ')
  return target ? `${label}: ${target}` : label
}

function IntegrationRow({
  icon: Icon,
  label,
  connected,
  unavailable,
  detail,
}: {
  icon: typeof Code2
  label: string
  connected: boolean
  unavailable: boolean
  detail?: string
}) {
  const color = unavailable ? '#f59e0b' : connected ? '#22c55e' : '#94a3b8'
  return (
    <div className="flex items-center gap-3 border-b border-border/55 py-3 last:border-b-0">
      <span
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl"
        style={{ background: `${color}14` }}
      >
        <Icon className="h-4 w-4" style={{ color }} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-xs font-semibold text-text-primary">{label}</div>
        {detail && <div className="mt-0.5 truncate text-[10px] text-text-muted">{detail}</div>}
      </div>
      {unavailable ? (
        <span className="text-[10px] font-medium text-amber-600">Unavailable</span>
      ) : connected ? (
        <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-600">
          <CheckCircle2 className="h-3 w-3" /> Connected
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 text-[10px] font-medium text-text-muted">
          <XCircle className="h-3 w-3" /> Not connected
        </span>
      )}
    </div>
  )
}

function ResourceBar({
  label,
  value,
  detail,
  color,
}: {
  label: string
  value: number | null | undefined
  detail?: string
  color: string
}) {
  const bounded = value == null ? 0 : Math.max(0, Math.min(100, value))
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between gap-3 text-xs">
        <span className="font-medium text-text-secondary">{label}</span>
        <span className="font-semibold text-text-primary">
          {value == null ? '—' : `${value}%`}
          {detail && <span className="ml-1.5 text-[10px] font-normal text-text-muted">{detail}</span>}
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-black/5 dark:bg-white/7">
        <div
          className="h-full rounded-full transition-[width] duration-500"
          style={{ width: `${bounded}%`, background: color }}
        />
      </div>
    </div>
  )
}

export function DashboardView() {
  const setSection = useAppStore(state => state.setActiveSection)
  const queryClient = useQueryClient()
  const [refreshing, setRefreshing] = useState(false)
  const environmentMode = useAppStore((state) => state.environmentMode)
  const dashboardEndpoint = typeof window !== 'undefined' ? window.location.origin : ''

  const { data: health, isError: healthError } = useHealth()
  const { data: security, isError: securityError } = useSecurity()
  const { data: approvals, isError: approvalsError } = useApprovals()
  const { data: tasks, isError: tasksError } = useTasks('pending', 20)
  const { data: selfAudit, isError: selfAuditError } = useSelfAuditSummary()
  const { data: actionLog, isError: actionLogError } = useActionLog(undefined, undefined, undefined, 8)
  const { data: incidents, isError: incidentsError } = useIncidents()
  const { data: catalogue, isError: catalogueError } = useMcpCatalogue()
  const { data: github, isError: githubError } = useGithubStatus()
  const { data: cloudflare, isError: cloudflareError } = useCfConnectionStatus()
  const { data: wordpress, isError: wordpressError } = useWordpressDeepStatus()
  const { data: vps, isError: vpsError } = useVpsResourceUsage()
  const { data: deployments, isError: deploymentsError } = useDeploymentsByDay()
  const { data: platformModel, isError: platformModelError } = usePlatformModel()
  const { data: pipeline, isError: pipelineError } = usePipelineQueue(undefined, undefined, 200)

  const workerList = health?.worker_statuses ?? []
  const totalWorkers = health?.total_workers
  const healthyWorkers = health?.healthy_count
  const degradedWorkers = workerList.filter(worker => worker.status === 'degraded').length
  const otherWorkers =
    totalWorkers != null && healthyWorkers != null
      ? Math.max(0, totalWorkers - healthyWorkers - degradedWorkers)
      : null

  const pendingApprovals = approvals?.approvals?.length
  const pendingTasks = tasks?.tasks?.length
  const openIncidents = incidents?.count
  const catalogueStale = !!catalogue?.generated_at && catalogue.snapshot_fresh === false
  const toolCount = catalogue?.snapshot_fresh ? catalogue.tool_count : null
  const recentActivity = actionLog?.entries ?? []
  const pipelineItems = pipeline?.items ?? []
  const pipelineInProgress = pipelineItems.filter(item => item.status === 'in_progress').length
  const pipelineAwaitingReview = pipelineItems.filter(item => item.status === 'awaiting_review').length
  const pipelinePaused = pipelineItems.filter(item => item.status === 'paused').length
  const pipelineCompleted = pipelineItems.filter(item => item.status === 'completed').length
  const pipelineRecent = pipelineItems.slice(0, 4)
  const pipelineStageCounts = pipelineItems.reduce<Record<string, number>>((counts, item) => {
    counts[item.current_stage] = (counts[item.current_stage] || 0) + 1
    return counts
  }, {})

  const blockedToday = security?.threats_blocked?.today
  const quarantined = security?.quarantine_entries?.length
  const tokenRevocations = security?.token_revocations

  const platformModelIssues = (() => {
    let errors = 0
    let warnings = 0
    if (platformModel?.items) {
      Object.values(platformModel.items).forEach(item => {
        ;(item.discrepancies || []).forEach(discrepancy => {
          if (discrepancy.severity === 'error') errors += 1
          else warnings += 1
        })
      })
    }
    return { errors, warnings }
  })()

  const deployByDay = deployments?.by_day ?? []
  const maxDeployments = Math.max(...deployByDay.map(day => day.deployments), 1)

  const healthColor =
    health?.status === 'healthy' ? '#22c55e'
      : health?.status === 'degraded' ? '#f59e0b'
        : '#ef4444'

  const healthDetail = healthError
    ? 'Live health endpoint unavailable'
    : health
      ? `${health.status} · checked ${shortTime(health.last_check)}`
      : 'Loading live health'

  async function refreshOverview() {
    setRefreshing(true)
    try {
      await queryClient.refetchQueries({ queryKey: ['dashboard'] })
    } finally {
      setRefreshing(false)
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-[1500px] flex-col gap-5 px-1 pb-8 pt-1 sm:px-2">
      <section
        className="relative overflow-hidden rounded-[1.6rem] border px-5 py-5 backdrop-blur-xl sm:px-6"
        style={{
          background: 'linear-gradient(135deg, var(--card-bg-start), var(--card-bg-end))',
          borderColor: 'var(--card-border)',
          boxShadow: 'var(--card-shadow)',
        }}
      >
        <div className="pointer-events-none absolute -right-16 -top-24 h-52 w-52 rounded-full bg-accent/8 blur-3xl" />
        <div className="relative flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-accent/15 bg-accent/8 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-accent">
              <Activity className="h-3.5 w-3.5" /> Live operations
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-text-primary sm:text-3xl">
              Platform Overview
            </h1>
            <p className="mt-2 max-w-xl text-sm leading-relaxed text-text-muted">
              A concise view of OPS health, attention items, activity and infrastructure. Every number below comes from a live or persisted platform source—no preview metrics.
            </p>
            <div className="mt-3 text-[10px] text-text-muted">
              Last health check: {healthError ? 'unavailable' : prettyTime(health?.last_check)}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {[
              ['Staff Portal', UserCog, 'team'],
              ['Automation', Zap, 'pipeline'],
              ['Workers', Server, 'workers'],
              ['Approvals', ClipboardList, 'governance'],
              ['Tasks', ListTodo, 'tasks'],
            ].map(([label, Icon, section]) => {
              const QuickIcon = Icon as typeof UserCog
              return (
                <button
                  key={label as string}
                  onClick={() => setSection(section as SidebarSection)}
                  className="inline-flex items-center gap-2 rounded-xl border border-border/80 bg-surface/55 px-3 py-2 text-xs font-medium text-text-secondary shadow-sm backdrop-blur-lg transition hover:border-accent/25 hover:text-accent"
                >
                  <QuickIcon className="h-3.5 w-3.5" /> {label as string}
                </button>
              )
            })}
            <button
              onClick={() => void refreshOverview()}
              disabled={refreshing}
              className="inline-flex items-center gap-2 rounded-xl bg-accent px-3.5 py-2 text-xs font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-60"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </section>

      <GlassCard accent={environmentMode === 'sandbox' ? '#f59e0b' : '#7c3aed'}>
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-text-muted">
              <Link2 className="h-4 w-4 text-accent" />
              Sandbox Control Endpoint
            </div>
            <div className="mt-2 break-all font-mono text-sm font-semibold text-text-primary">
              {dashboardEndpoint || 'Dashboard endpoint unavailable'}
            </div>
            <p className="mt-1 text-[11px] leading-relaxed text-text-muted">
              {environmentMode === 'sandbox'
                ? 'Your authenticated dashboard is currently in Sandbox Mode. Production writes are blocked unless explicitly sandbox-enabled.'
                : 'Sandbox Mode uses this same signed-in dashboard. Enter Sandbox from the permanent environment bar above — no second login is required.'}
            </p>
          </div>
          <span className={`inline-flex shrink-0 items-center rounded-full border px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.1em] ${
            environmentMode === 'sandbox'
              ? 'border-amber-400/30 bg-amber-400/10 text-amber-500'
              : 'border-emerald-400/25 bg-emerald-400/8 text-emerald-500'
          }`}>
            {environmentMode === 'sandbox' ? 'Sandbox Active' : 'Live Active'}
          </span>
        </div>
      </GlassCard>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6 sm:gap-3">
        <MetricCard
          icon={Activity}
          label="Platform health"
          value={healthError ? '—' : health ? `${health.platform_health}%` : '…'}
          detail={healthDetail}
          color={healthColor}
          unavailable={healthError}
          onClick={() => setSection('workers')}
        />
        <MetricCard
          icon={Server}
          label="Workers"
          value={healthError ? '—' : totalWorkers ?? '…'}
          detail={
            healthError
              ? 'Worker health unavailable'
              : healthyWorkers == null
                ? 'Loading worker state'
                : `${healthyWorkers} healthy · ${degradedWorkers} degraded${otherWorkers ? ` · ${otherWorkers} other` : ''}`
          }
          color="#60a5fa"
          unavailable={healthError}
          onClick={() => setSection('workers')}
        />
        <MetricCard
          icon={ClipboardList}
          label="Pending approvals"
          value={approvalsError ? '—' : pendingApprovals ?? '…'}
          detail={approvalsError ? 'Approval queue unavailable' : pendingApprovals === 0 ? 'Nothing awaiting review' : 'Requires operator review'}
          color={pendingApprovals ? '#f59e0b' : '#22c55e'}
          unavailable={approvalsError}
          onClick={() => setSection('governance')}
        />
        <MetricCard
          icon={ListTodo}
          label="Open tasks"
          value={tasksError ? '—' : pendingTasks ?? '…'}
          detail={tasksError ? 'Task source unavailable' : 'Pending operational tasks'}
          color="#a78bfa"
          unavailable={tasksError}
          onClick={() => setSection('tasks')}
        />
        <MetricCard
          icon={AlertTriangle}
          label="Open incidents"
          value={incidentsError ? '—' : openIncidents ?? '…'}
          detail={incidentsError ? 'Incident registry unavailable' : openIncidents === 0 ? 'No active incidents' : 'Open / investigating / mitigating'}
          color={openIncidents ? '#ef4444' : '#22c55e'}
          unavailable={incidentsError}
          onClick={() => setSection('governance')}
        />
        <MetricCard
          icon={Cpu}
          label="MCP tools"
          value={catalogueError || toolCount == null ? '—' : toolCount}
          detail={
            catalogueError
              ? 'Catalogue unavailable'
              : catalogueStale
                ? `Catalogue stale · last snapshot ${prettyTime(catalogue?.generated_at)}`
                : catalogue?.generated_at
                  ? `Snapshot ${shortTime(catalogue.generated_at)}`
                  : 'Catalogue has not been refreshed'
          }
          color="#06b6d4"
          unavailable={catalogueError || catalogueStale || toolCount == null}
          onClick={() => setSection('toolsregistry')}
        />
      </div>

      <GlassCard accent="#8b5cf6">
        <SectionHeading
          title="Automation Pipeline"
          description="Live idea → planning → design → build → approval workflow from OpsBridge D1."
          action="Open Automation"
          onAction={() => setSection('pipeline')}
        />
        {pipelineError ? (
          <div className="rounded-xl border border-amber-500/15 bg-amber-500/5 p-4 text-xs text-amber-600">
            Automation Pipeline is currently unavailable.
          </div>
        ) : !pipeline ? (
          <div className="py-8 text-center text-xs text-text-muted">Loading live pipeline…</div>
        ) : (
          <div className="grid gap-4 xl:grid-cols-[.8fr_1.2fr]">
            <div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                {[
                  ['Queue', pipeline.count, '#8b5cf6'],
                  ['In progress', pipelineInProgress, '#3b82f6'],
                  ['Awaiting review', pipelineAwaitingReview, '#f59e0b'],
                  ['Paused', pipelinePaused, '#94a3b8'],
                  ['Completed', pipelineCompleted, '#22c55e'],
                ].map(([label, value, color]) => (
                  <div key={label as string} className="rounded-xl border border-border/55 bg-bg/30 p-3">
                    <div className="text-lg font-semibold text-text-primary">{value as number}</div>
                    <div className="mt-1 flex items-center gap-1.5 text-[9px] text-text-muted">
                      <span className="h-1.5 w-1.5 rounded-full" style={{ background: color as string }} />
                      {label as string}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-3 rounded-xl border border-border/55 bg-bg/25 p-3">
                <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-text-muted">Current stages</div>
                <div className="flex flex-wrap gap-2">
                  {(['idea', 'planning', 'design', 'build', 'approval'] as const).map(stage => (
                    <span key={stage} className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-surface/45 px-2.5 py-1 text-[10px] text-text-secondary">
                      <span className="capitalize">{stage}</span>
                      <strong className="font-semibold text-text-primary">{pipelineStageCounts[stage] || 0}</strong>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-border/55 bg-bg/25 px-3">
              <div className="flex items-center justify-between border-b border-border/45 py-2.5">
                <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-text-muted">Recent pipeline items</span>
                <span className="inline-flex items-center gap-1 text-[9px] text-emerald-600">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Live · refreshes every 15s
                </span>
              </div>
              {pipelineRecent.length === 0 ? (
                <div className="py-8 text-center text-xs text-text-muted">No pipeline items.</div>
              ) : (
                pipelineRecent.map(item => {
                  const statusColor =
                    item.status === 'completed' ? '#22c55e'
                      : item.status === 'awaiting_review' ? '#f59e0b'
                        : item.status === 'paused' ? '#94a3b8'
                          : item.status === 'rejected' || item.status === 'failed' ? '#ef4444'
                            : '#3b82f6'
                  return (
                    <button
                      key={item.id}
                      onClick={() => setSection('pipeline')}
                      className="flex w-full items-center gap-3 border-b border-border/45 py-2.5 text-left last:border-b-0 hover:bg-surface-elevated/30"
                    >
                      <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: statusColor }} />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-[11px] font-medium text-text-primary">
                          {item.idea_number != null ? `#${item.idea_number} · ` : ''}{item.idea_title}
                        </div>
                        <div className="mt-0.5 flex items-center gap-2 text-[9px] text-text-muted">
                          <span className="capitalize">{item.current_stage}</span>
                          <span>·</span>
                          <span className="capitalize">{item.status.replaceAll('_', ' ')}</span>
                          {item.previous_rejections ? <><span>·</span><span>{item.previous_rejections} prior rejection{item.previous_rejections === 1 ? '' : 's'}</span></> : null}
                        </div>
                      </div>
                      <span className="shrink-0 text-[9px] text-text-muted">{shortTime(new Date(item.updated_at).toISOString())}</span>
                      <ChevronRight className="h-3.5 w-3.5 shrink-0 text-text-muted/55" />
                    </button>
                  )
                })
              )}
            </div>
          </div>
        )}
      </GlassCard>

      <div className="grid gap-4 xl:grid-cols-[1.05fr_1fr_1fr]">
        <GlassCard accent="#f59e0b">
          <SectionHeading title="Needs attention" description="Only current actionable or security state." />
          <div className="space-y-2">
            {[
              {
                label: 'Approvals waiting',
                value: approvalsError ? '—' : pendingApprovals ?? '…',
                detail: approvalsError ? 'Unavailable' : 'Tier / operation approvals',
                color: pendingApprovals ? '#f59e0b' : '#22c55e',
                section: 'governance',
              },
              {
                label: 'Open incidents',
                value: incidentsError ? '—' : openIncidents ?? '…',
                detail: incidentsError ? 'Unavailable' : 'Incident registry',
                color: openIncidents ? '#ef4444' : '#22c55e',
                section: 'governance',
              },
              {
                label: 'Self-audit critical',
                value: selfAuditError ? '—' : selfAudit?.summary?.critical ?? '…',
                detail: selfAuditError ? 'Unavailable' : `${selfAudit?.summary?.awaiting_review ?? 0} awaiting review`,
                color: selfAudit?.summary?.critical ? '#ef4444' : '#22c55e',
                section: 'selfaudit',
              },
              {
                label: 'Blocked security events today',
                value: securityError ? '—' : blockedToday ?? '…',
                detail: securityError ? 'Unavailable' : `${quarantined ?? 0} active quarantine · ${tokenRevocations ?? 0} token revocations (30d)`,
                color: blockedToday ? '#f59e0b' : '#22c55e',
                section: 'security',
              },
            ].map(item => (
              <button
                key={item.label}
                onClick={() => setSection(item.section as SidebarSection)}
                className="flex w-full items-center gap-3 rounded-xl border border-border/55 bg-bg/30 px-3 py-2.5 text-left transition hover:border-accent/20 hover:bg-surface-elevated/45"
              >
                <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: item.color }} />
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium text-text-primary">{item.label}</div>
                  <div className="mt-0.5 truncate text-[10px] text-text-muted">{item.detail}</div>
                </div>
                <span className="text-sm font-semibold text-text-primary">{item.value}</span>
                <ChevronRight className="h-3.5 w-3.5 text-text-muted/60" />
              </button>
            ))}
          </div>
        </GlassCard>

        <GlassCard accent="#60a5fa">
          <SectionHeading title="Workers" description="Single live health sweep—no duplicate workers request." action="View all" onAction={() => setSection('workers')} />
          {healthError ? (
            <div className="py-8 text-center text-xs text-amber-600">Worker health is currently unavailable.</div>
          ) : workerList.length === 0 ? (
            <div className="py-8 text-center text-xs text-text-muted">Loading worker health…</div>
          ) : (
            <div className="space-y-0">
              {workerList.slice(0, 7).map(worker => (
                <div key={worker.name} className="flex items-center gap-2 border-b border-border/45 py-2.5 last:border-b-0">
                  <Dot status={worker.status} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-mono text-[11px] text-text-primary">{worker.name}</div>
                    <div className="text-[9px] text-text-muted">{worker.ecosystem}</div>
                  </div>
                  {worker.latency_ms != null && <span className="text-[9px] text-text-muted">{worker.latency_ms}ms</span>}
                  <span className="text-[9px] font-medium capitalize text-text-secondary">{worker.status}</span>
                </div>
              ))}
              {workerList.length > 7 && (
                <div className="pt-2 text-center text-[10px] text-text-muted">+{workerList.length - 7} more workers</div>
              )}
            </div>
          )}
        </GlassCard>

        <GlassCard>
          <SectionHeading title="Recent activity" description="Latest persisted action-log entries." action="View all" onAction={() => setSection('actionlog')} />
          {actionLogError ? (
            <div className="py-8 text-center text-xs text-amber-600">Action log unavailable.</div>
          ) : recentActivity.length === 0 ? (
            <div className="py-8 text-center text-xs text-text-muted">No recent recorded activity.</div>
          ) : (
            <div className="space-y-0">
              {recentActivity.slice(0, 7).map(entry => (
                <div key={entry.action_id} className="flex items-center gap-2 border-b border-border/45 py-2.5 last:border-b-0">
                  <span
                    className="h-2 w-2 shrink-0 rounded-full"
                    style={{
                      background:
                        entry.result === 'success' ? '#22c55e'
                          : entry.result === 'failure' ? '#ef4444'
                            : '#94a3b8',
                    }}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[11px] font-medium text-text-primary">{prettifyAction(entry.action, entry.target)}</div>
                    <div className="truncate text-[9px] text-text-muted">{entry.actor}</div>
                  </div>
                  <span className="text-[9px] text-text-muted">{shortTime(entry.timestamp)}</span>
                </div>
              ))}
            </div>
          )}
        </GlassCard>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1.25fr_.75fr]">
        <GlassCard
          accent={
            platformModelError ? '#f59e0b'
              : platformModel?.model_status === 'current' ? '#22c55e'
                : platformModel?.model_status === 'stale' ? '#f59e0b'
                  : '#ef4444'
          }
          onClick={() => setSection('platformmodel')}
        >
          <SectionHeading title="Platform model" description="Current discovered structure of the OPS platform." action="Open model" onAction={() => setSection('platformmodel')} />
          {platformModelError ? (
            <div className="rounded-xl border border-amber-500/15 bg-amber-500/5 p-4 text-xs text-amber-600">
              Platform Model is currently unavailable.
            </div>
          ) : !platformModel ? (
            <div className="py-8 text-center text-xs text-text-muted">Loading Platform Model…</div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="rounded-xl border border-border/55 bg-bg/30 p-3">
                <div className="text-lg font-semibold text-text-primary">{platformModel.coverage?.workers ?? Object.keys(platformModel.items).length}</div>
                <div className="mt-1 text-[10px] text-text-muted">Workers covered</div>
              </div>
              <div className="rounded-xl border border-border/55 bg-bg/30 p-3">
                <div className="text-lg font-semibold text-text-primary">{platformModel.coverage?.relationships_found ?? '—'}</div>
                <div className="mt-1 text-[10px] text-text-muted">Relationships</div>
              </div>
              <div className="rounded-xl border border-border/55 bg-bg/30 p-3">
                <div className={`text-lg font-semibold ${platformModelIssues.errors ? 'text-red-500' : 'text-text-primary'}`}>{platformModelIssues.errors}</div>
                <div className="mt-1 text-[10px] text-text-muted">Errors</div>
              </div>
              <div className="rounded-xl border border-border/55 bg-bg/30 p-3">
                <div className={`text-lg font-semibold ${platformModelIssues.warnings ? 'text-amber-600' : 'text-text-primary'}`}>{platformModelIssues.warnings}</div>
                <div className="mt-1 text-[10px] text-text-muted">Warnings</div>
              </div>
              <div className="col-span-2 flex items-center justify-between rounded-xl border border-border/55 bg-bg/30 px-3 py-2.5 sm:col-span-4">
                <div>
                  <div className="text-[10px] text-text-muted">Model status</div>
                  <div className="mt-0.5 text-xs font-semibold capitalize text-text-primary">{platformModel.model_status}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] text-text-muted">Generated</div>
                  <div className="mt-0.5 text-[10px] text-text-secondary">{prettyTime(platformModel.generated_at)}</div>
                </div>
              </div>
            </div>
          )}
        </GlassCard>

        <GlassCard>
          <SectionHeading title="Integrations" description="Live connection state—not inferred from configuration." />
          <IntegrationRow
            icon={Code2}
            label="GitHub"
            connected={github?.connected ?? false}
            unavailable={githubError}
            detail={github?.account_login ?? undefined}
          />
          <IntegrationRow
            icon={Cloud}
            label="Cloudflare"
            connected={cloudflare?.connected ?? false}
            unavailable={cloudflareError}
            detail={cloudflare?.connected ? `${cloudflare.worker_count ?? 0} workers visible` : undefined}
          />
          <IntegrationRow
            icon={Globe}
            label="WordPress"
            connected={wordpress?.connected ?? false}
            unavailable={wordpressError}
            detail={wordpress?.site_url || wordpress?.title || undefined}
          />
        </GlassCard>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <GlassCard accent="#a78bfa">
          <SectionHeading title="VPS resources" description="Live CPU, memory, disk and cumulative network transfer." />
          {vpsError ? (
            <div className="py-8 text-center text-xs text-amber-600">VPS resource metrics unavailable.</div>
          ) : (
            <div className="space-y-4">
              <ResourceBar label="CPU" value={vps?.cpu?.percent} color="#60a5fa" />
              <ResourceBar
                label="Memory"
                value={vps?.memory?.percent}
                color="#a78bfa"
                detail={vps?.memory?.used_mb != null ? `${vps.memory.used_mb}MB / ${vps.memory.total_mb}MB` : undefined}
              />
              <ResourceBar
                label="Disk"
                value={vps?.disk?.percent}
                color="#f59e0b"
                detail={vps?.disk?.used ? `${vps.disk.used} / ${vps.disk.total}` : undefined}
              />
              <div className="flex items-center justify-between border-t border-border/55 pt-3 text-[10px]">
                <span className="text-text-muted">Network since VPS boot</span>
                <span className="font-medium text-text-secondary">
                  ↓ {vps?.network?.rx_gb ?? '—'}GB · ↑ {vps?.network?.tx_gb ?? '—'}GB
                </span>
              </div>
            </div>
          )}
        </GlassCard>

        <GlassCard accent="#06b6d4">
          <SectionHeading title="Deployments" description="Verified deployment events recorded over the last seven days." />
          {deploymentsError ? (
            <div className="py-8 text-center text-xs text-amber-600">Deployment history unavailable.</div>
          ) : deployByDay.length === 0 ? (
            <div className="py-8 text-center text-xs text-text-muted">No deployments recorded in the last seven days.</div>
          ) : (
            <div className="flex h-40 items-end gap-2 pt-4">
              {deployByDay.map(day => {
                const height = Math.max((day.deployments / maxDeployments) * 105, day.deployments > 0 ? 5 : 0)
                let label = day.day
                try {
                  label = new Date(`${day.day}T00:00:00Z`).toLocaleDateString('en-GB', { weekday: 'short' })
                } catch {
                  // keep persisted date
                }
                return (
                  <div key={day.day} className="flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-1.5">
                    <span className="text-[10px] font-semibold text-text-secondary">{day.deployments}</span>
                    <div
                      className="w-full max-w-8 rounded-t-md bg-accent/80 transition-[height] duration-500"
                      style={{ height }}
                    />
                    <span className="text-[9px] text-text-muted">{label}</span>
                  </div>
                )
              })}
            </div>
          )}
          <button
            onClick={() => setSection('actionlog')}
            className="mt-3 inline-flex items-center gap-1 text-[10px] font-medium text-accent hover:underline"
          >
            View deployment activity <ChevronRight className="h-3 w-3" />
          </button>
        </GlassCard>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <GlassCard onClick={() => setSection('security')} accent="#22c55e">
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-5 w-5 text-emerald-500" />
            <div>
              <div className="text-sm font-semibold text-text-primary">
                {securityError ? 'Security unavailable' : `${blockedToday ?? 0} blocked today`}
              </div>
              <div className="mt-0.5 text-[10px] text-text-muted">Active quarantine: {securityError ? '—' : quarantined ?? 0}</div>
            </div>
          </div>
        </GlassCard>
        <GlassCard onClick={() => setSection('selfaudit')} accent="#60a5fa">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-5 w-5 text-blue-500" />
            <div>
              <div className="text-sm font-semibold text-text-primary">
                {selfAuditError ? 'Self-audit unavailable' : `${selfAudit?.summary?.critical ?? 0} critical findings`}
              </div>
              <div className="mt-0.5 text-[10px] text-text-muted">
                Last run: {selfAuditError ? '—' : prettyTime(selfAudit?.summary?.last_run)}
              </div>
            </div>
          </div>
        </GlassCard>
        <GlassCard onClick={() => setSection('actionlog')} accent="#a78bfa">
          <div className="flex items-center gap-3">
            <Rocket className="h-5 w-5 text-violet-500" />
            <div>
              <div className="text-sm font-semibold text-text-primary">
                {deploymentsError ? 'Deployment history unavailable' : `${deployByDay.reduce((sum, day) => sum + day.deployments, 0)} deployments`}
              </div>
              <div className="mt-0.5 text-[10px] text-text-muted">Recorded in the last seven days</div>
            </div>
          </div>
        </GlassCard>
      </div>

      <div className="flex items-center justify-between rounded-xl border border-border/55 bg-surface/35 px-3 py-2 text-[9px] text-text-muted backdrop-blur-lg">
        <span>Overview intentionally excludes preview and synthetic metrics.</span>
        <span className="inline-flex items-center gap-1">
          <HardDrive className="h-3 w-3" /> Live + persisted sources
        </span>
      </div>
    </div>
  )
}
